import { useCallback, useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import {
  ArrowSquareOutIcon,
  ListChecksIcon,
  ReceiptIcon,
  StorefrontIcon,
  WarningIcon,
  XCircleIcon,
} from '@phosphor-icons/react/dist/ssr';

import { AuthLoadingScreen } from '@/components/templates';
import { ScreenShell } from '@/components/screen-shell';
import { NavBar } from '@/components/nav-bar';
import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert';
import { Badge } from '@/components/ui/badge';
import { Button, buttonVariants } from '@/components/ui/button';
import {
  Card,
  CardContent,
  CardEyebrow,
  CardHeader,
} from '@/components/ui/card';
import { KeyValue } from '@/components/ui/key-value';
import { cn } from '@/lib/utils';

import {
  cancelOrder,
  listOrders,
  type OrderStatus,
  type OrderSummary,
} from '../api';
import { useAuth } from '../auth';
import { useT, type TFunction } from '../i18n';
import { useTelegramBackButton } from '../lib/telegram-back-button';
import { useNavStackEntry } from '../lib/nav-stack';

type FilterStatus = OrderStatus | 'ALL';

const FILTERS: { key: FilterStatus; tKey: string }[] = [
  { key: 'OPEN', tKey: 'orders.filter_open' },
  { key: 'PENDING', tKey: 'orders.filter_pending' },
  { key: 'MATCHED', tKey: 'orders.filter_matched' },
  { key: 'CANCELLED', tKey: 'orders.filter_cancelled' },
  { key: 'FAILED', tKey: 'orders.filter_failed' },
  { key: 'ALL', tKey: 'orders.filter_all' },
];

function statusTone(s: OrderStatus): 'primary' | 'info' | 'success' | 'warning' | 'danger' | 'neutral' {
  switch (s) {
    case 'OPEN':
      return 'primary';
    case 'PENDING':
      return 'warning';
    case 'MATCHED':
      return 'success';
    case 'CANCELLED':
      return 'neutral';
    case 'FAILED':
      return 'danger';
    default:
      return 'neutral';
  }
}

function statusLabel(t: TFunction, s: OrderStatus): string {
  switch (s) {
    case 'OPEN':
      return t('orders.status_open');
    case 'PENDING':
      return t('orders.status_pending');
    case 'MATCHED':
      return t('orders.status_matched');
    case 'CANCELLED':
      return t('orders.status_cancelled');
    case 'FAILED':
      return t('orders.status_failed');
    default:
      return s;
  }
}

function filterLabelLower(t: TFunction, f: FilterStatus): string {
  if (f === 'ALL') return '';
  return statusLabel(t, f).toLowerCase();
}

function fmtCents(price: string): string {
  const n = Number(price);
  if (!Number.isFinite(n) || n <= 0) return '—';
  return `${(n * 100).toFixed(1)}\u00a2`;
}

function fmtSize(size: string): string {
  const n = Number(size);
  if (!Number.isFinite(n)) return size;
  return n.toLocaleString(undefined, { maximumFractionDigits: 2 });
}

function fmtTotalUsd(price: string, size: string): string {
  const p = Number(price);
  const s = Number(size);
  if (!Number.isFinite(p) || !Number.isFinite(s)) return '—';
  return `$${(p * s).toFixed(2)}`;
}

function fmtDateTime(iso: string): string {
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return iso;
  return d.toLocaleString(undefined, {
    month: 'short',
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });
}

function truncSlug(slug: string | null): string {
  if (!slug) return '—';
  return slug.length > 48 ? `${slug.slice(0, 45)}…` : slug;
}

function orderTitle(order: OrderSummary): string {
  if (order.marketQuestion && order.marketQuestion.trim().length > 0) {
    return order.marketQuestion;
  }
  return truncSlug(order.marketSlug);
}

export default function Orders() {
  const t = useT();
  const auth = useAuth();
  useTelegramBackButton();
  useNavStackEntry({ label: t('orders.breadcrumb') });

  const [filter, setFilter] = useState<FilterStatus>('OPEN');
  const [orders, setOrders] = useState<OrderSummary[] | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [cancellingId, setCancellingId] = useState<string | null>(null);
  const [cancelError, setCancelError] = useState<string | null>(null);

  const ready = auth.state === 'ready';

  const reload = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const params = filter === 'ALL' ? {} : { status: filter };
      const data = await listOrders(params);
      setOrders(data);
    } catch (err) {
      setError((err as Error).message);
    } finally {
      setLoading(false);
    }
  }, [filter]);

  useEffect(() => {
    if (!ready) return;
    let cancelled = false;
    setLoading(true);
    setError(null);
    (async () => {
      try {
        const params = filter === 'ALL' ? {} : { status: filter };
        const data = await listOrders(params);
        if (!cancelled) setOrders(data);
      } catch (err) {
        if (!cancelled) setError((err as Error).message);
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [ready, filter]);

  const onCancel = useCallback(
    async (id: string) => {
      if (!window.confirm(t('orders.cancel_confirm'))) return;
      setCancellingId(id);
      setCancelError(null);
      try {
        await cancelOrder(id);
        await reload();
      } catch (err) {
        setCancelError((err as Error).message);
      } finally {
        setCancellingId(null);
      }
    },
    [reload, t],
  );

  const eyebrow = useMemo(
    () => (
      <span className="inline-flex items-center gap-1.5">
        <ReceiptIcon weight="fill" className="size-3" />
        {t('orders.eyebrow')}
      </span>
    ),
    [t],
  );

  if (auth.state === 'loading' || (loading && orders === null)) {
    return (
      <AuthLoadingScreen
        title={t('orders.loading_title')}
        description={t('orders.loading_desc')}
        shortDescription={t('orders.loading_short')}
        steps={[
          { label: 'Session', color: 'bg-emerald-500' },
          { label: t('orders.eyebrow'), color: 'bg-primary', delay: '0.4s' },
        ]}
      />
    );
  }

  if (auth.state === 'error') {
    return (
      <ScreenShell eyebrow={eyebrow} title={t('orders.title')}>
        <NavBar />
        <Alert tone="danger">
          <WarningIcon weight="fill" className="size-4" />
          <AlertTitle>{t('common.auth_error', { error: '' })}</AlertTitle>
          <AlertDescription>{auth.error}</AlertDescription>
        </Alert>
      </ScreenShell>
    );
  }

  return (
    <ScreenShell
      eyebrow={eyebrow}
      title={t('orders.title')}
      description={t('orders.description')}
    >
      <NavBar />

      <Card>
        <CardHeader>
          <CardEyebrow>
            <ListChecksIcon weight="fill" className="size-3" />
            {t('orders.eyebrow')}
          </CardEyebrow>
        </CardHeader>
        <CardContent>
          <div className="flex flex-wrap gap-1.5">
            {FILTERS.map((f) => {
              const active = f.key === filter;
              return (
                <button
                  key={f.key}
                  type="button"
                  onClick={() => setFilter(f.key)}
                  className={cn(
                    'rounded-full px-3 py-1 text-[12px] font-medium ring-1 transition-colors',
                    active
                      ? 'bg-primary text-primary-foreground ring-primary/40'
                      : 'bg-foreground/5 text-muted-foreground ring-foreground/10 hover:text-foreground hover:bg-foreground/10',
                  )}
                >
                  {t(f.tKey)}
                </button>
              );
            })}
          </div>
        </CardContent>
      </Card>

      {error ? (
        <Alert tone="danger">
          <WarningIcon weight="fill" className="size-4" />
          <AlertTitle>{t('orders.error_title')}</AlertTitle>
          <AlertDescription>{error}</AlertDescription>
        </Alert>
      ) : null}

      {cancelError ? (
        <Alert tone="danger">
          <WarningIcon weight="fill" className="size-4" />
          <AlertTitle>{t('orders.cancel_failed_title')}</AlertTitle>
          <AlertDescription>{cancelError}</AlertDescription>
        </Alert>
      ) : null}

      {!error && orders && orders.length === 0 ? (
        <Card>
          <CardContent className="flex flex-col items-center gap-3 py-6 text-center">
            <span className="bg-primary/12 ring-primary/25 inline-flex size-12 items-center justify-center rounded-2xl ring-1">
              <ReceiptIcon weight="fill" className="text-primary size-6" />
            </span>
            <h3 className="text-foreground text-[15px] font-semibold">
              {t('orders.empty_title', { status: filterLabelLower(t, filter) })}
            </h3>
            <p className="text-muted-foreground text-[13px] leading-relaxed">
              {t('orders.empty_intro')}
            </p>
            <Link
              to="/markets"
              className={cn(
                buttonVariants({ size: 'lg' }),
                'justify-center mt-1',
              )}
            >
              <StorefrontIcon weight="bold" className="size-4" />
              {t('orders.empty_cta_markets')}
            </Link>
          </CardContent>
        </Card>
      ) : null}

      {orders && orders.length > 0 ? (
        <div className="flex flex-col gap-3">
          {orders.map((o) => (
            <OrderCard
              key={o.id}
              order={o}
              t={t}
              cancelling={cancellingId === o.id}
              onCancel={onCancel}
            />
          ))}
        </div>
      ) : null}
    </ScreenShell>
  );
}

function OrderCard({
  order,
  t,
  cancelling,
  onCancel,
}: {
  order: OrderSummary;
  t: TFunction;
  cancelling: boolean;
  onCancel: (id: string) => void;
}) {
  const canCancel = order.status === 'OPEN' || order.status === 'PENDING';
  const sideLabel =
    order.side === 'BUY' ? t('orders.side_buy') : t('orders.side_sell');
  const sideTone: 'primary' | 'danger' =
    order.side === 'BUY' ? 'primary' : 'danger';
  const title = orderTitle(order);

  return (
    <div className="bg-card/85 supports-backdrop-filter:bg-card/80 ring-foreground/10 relative flex w-full flex-col gap-3 overflow-hidden rounded-3xl p-4 ring-1 backdrop-blur-md sm:p-5">
      <div className="flex items-start gap-3">
        <span className="bg-primary/12 ring-primary/25 inline-flex size-10 shrink-0 items-center justify-center rounded-xl ring-1">
          <ReceiptIcon weight="fill" className="text-primary size-5" />
        </span>
        <div className="flex min-w-0 flex-1 flex-col gap-1">
          {order.marketSlug ? (
            <Link
              to={`/markets/m/${encodeURIComponent(order.marketSlug)}`}
              className="group text-foreground hover:text-primary inline-flex min-w-0 items-center gap-1 text-[14px] font-semibold leading-snug tracking-tight transition-colors"
            >
              <span className="line-clamp-2">{title}</span>
              <ArrowSquareOutIcon
                weight="bold"
                className="text-muted-foreground size-3 shrink-0 opacity-60 transition-opacity group-hover:opacity-100"
                aria-hidden
              />
            </Link>
          ) : (
            <span className="text-muted-foreground text-[14px] font-semibold leading-snug">
              {title}
            </span>
          )}
          <div className="flex flex-wrap items-center gap-1.5 text-[11px]">
            <Badge tone={sideTone}>{sideLabel}</Badge>
            {order.outcome ? (
              <Badge tone="primary">{order.outcome}</Badge>
            ) : null}
            <Badge tone={statusTone(order.status)}>
              {statusLabel(t, order.status)}
            </Badge>
            <span className="text-muted-foreground tabular-nums">
              {order.orderType}
            </span>
          </div>
        </div>
      </div>

      <div className="grid grid-cols-3 gap-2">
        <KeyValue
          label={t('orders.size_label')}
          value={fmtSize(order.size)}
          mono
        />
        <KeyValue
          label={t('orders.price_label')}
          value={fmtCents(order.price)}
          mono
        />
        <KeyValue
          label={t('orders.total_label')}
          value={fmtTotalUsd(order.price, order.size)}
          mono
        />
      </div>

      <div className="text-muted-foreground flex items-center justify-between gap-3 text-[11px] tabular-nums">
        <span>
          {t('orders.created_label')}:{' '}
          <span className="text-foreground">{fmtDateTime(order.createdAt)}</span>
        </span>
      </div>

      {order.errorMessage ? (
        <p className="text-rose-500 break-words text-[12px]">
          {order.errorMessage}
        </p>
      ) : null}

      {canCancel ? (
        <Button
          type="button"
          variant="outline"
          size="sm"
          disabled={cancelling}
          onClick={() => onCancel(order.id)}
          className="self-end"
        >
          <XCircleIcon weight="bold" className="size-4" />
          {cancelling ? t('orders.cancelling') : t('orders.cancel_btn')}
        </Button>
      ) : null}
    </div>
  );
}
